import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
  Tag,
} from "antd";
import { PageHeader } from "../../../../components/page-header/page-header";
import useFetch from "../../../../hooks/useFetch";
import {
  GetDefectLogBook,
  GetSavedDefectLogBookFile,
} from "../../../../utils/api.constant";
import { eREGIONS, eDATE_FORMAT, eResultCode } from "../../../../utils/enum";
import useToast from "../../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../../state/toast/slice";
import CustomDrawer from "../../../../components/drawer";
import FaultCategoryForm from "./form";
import { UserContext } from "../../../../context";
import { TContext, TFilterModel } from "../../../../types/config";
import {
  CloseCircleFilled,
  FilterOutlined,
  IdcardFilled,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import utils from "../../../../utils";
import ColumnManager from "../../../../components/configurable-columns";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import { defaultFilterParams } from "../../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../../utils/column-management";
import { TDefectLogBook } from "../../../../types";
import { useColumnSearch } from "../../../../components/table/column-search";
import AuthUtil from "../../../../utils/auth";
import saveAs from "file-saver";

const FAULT_LIST_COLUMN_KEY = "defectLogBookList";

export default function DefectLogBookList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TDefectLogBook[]>([]);
  const context: TContext = useContext(UserContext);
  const { getDateRangeFilterProps } = useColumnSearch<TDefectLogBook>();
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "originalFileName",
      key: "originalFileName",
      // width: 450,
      sorter: (a: TDefectLogBook, b: TDefectLogBook) =>
        a.originalFileName?.localeCompare?.(b.originalFileName),
      onFilter: (value: string | number | boolean, record: TDefectLogBook) =>
        record.originalFileName?.toLowerCase() ===
        (value as string).toLowerCase(),
      render: (text: string, record: TDefectLogBook) => {
        const handleDownload = async (
          e: React.MouseEvent<HTMLAnchorElement>,
        ) => {
          e.preventDefault();
          if (!record.id) return;

          try {
            setIsLoading(true);
            const response = await fetch(
              `${
                process.env.REACT_APP_PUBLIC_API_BASEURL
              }${GetSavedDefectLogBookFile}?id=${record.id}`,
              {
                method: "GET",
                headers: {
                  ...AuthUtil.getToken(),
                },
              },
            );

            if (response.ok) {
              const blob = await response.blob();
              saveAs(blob, record.originalFileName);
            } else {
              onShowToast({
                type: ToastType.error,
                title: <CloseCircleFilled />,
                position: ToastOpen.leftBottom,
                content: "Failed to download file",
              });
            }
          } catch (error) {
            onShowToast({
              type: ToastType.error,
              title: <CloseCircleFilled />,
              position: ToastOpen.leftBottom,
              content: "An error occurred while downloading the file",
            });
          } finally {
            setIsLoading(false);
          }
        };

        return record.id ? (
          <a href="#" onClick={handleDownload} style={{ cursor: "pointer" }}>
            {text}
          </a>
        ) : (
          text
        );
      },
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      // width: 250,
      sorter: (a: TDefectLogBook, b: TDefectLogBook) =>
        a.customerName?.localeCompare?.(b.customerName),
      filters: utils.getUniqueFilters(data, "customerName"),
      onFilter: (value: string | number | boolean, record: TDefectLogBook) =>
        record.customerName?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Uploaded On",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: 150,
      ...getDateRangeFilterProps("createdOn"),
      sorter: (a: TDefectLogBook, b: TDefectLogBook) =>
        new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
      render: (createdOn: string) => {
        if (createdOn) {
          return DateUtils.format(createdOn, context?.user?.dateFormat, true);
        }
        return "-"; // Or any placeholder for empty values
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      // width: 120,
      sorter: (a: TDefectLogBook, b: TDefectLogBook) =>
        Number(a.isActive) - Number(b.isActive),
      filters: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      onFilter: (value: string | number | boolean, record: TDefectLogBook) =>
        record.isActive === value,
      render: (isActive: number) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        FAULT_LIST_COLUMN_KEY,
      );
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col]),
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap?.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetDefectLogBook, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data?.map(
          (item: TDefectLogBook, index: number) => ({
            ...item,
            key: (item.id ?? "").toString(),
            srNo: index + 1,
          }),
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(formattedData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch Defect Code Book Data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TDefectLogBook[]) => {
    const getFilters = (key: keyof TDefectLogBook) => {
      const uniqueValues = Array.from(
        new Set(
          dataset
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-",
            ) as string[],
        ),
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };
    const nameFilters = getFilters("originalFileName");
    const customerNameFilters = getFilters("customerName");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "originalFileName") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "customerName") {
          return { ...col, filters: customerNameFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data, searchText]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const lowerSearch = (searchText || "").toLowerCase();
    const searchMatch = Object.values(item).some(
      (val) => val && val.toString().toLowerCase().includes(lowerSearch),
    );

    const filtersMatch = Object.entries(activeFilters).every(([key, value]) => {
      if (!value) return true;
      const allowed = Array.isArray(value) ? value : [value];
      if (allowed.length === 0) return true;
      const itemVal = (item as any)[key];
      return allowed.map(String).includes(String(itemVal));
    });

    return searchMatch && filtersMatch;
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns);
    clearColumnPreferences(FAULT_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns);
    saveColumnPreferences(FAULT_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false);
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item))),
  );

  const exportData = data.map((user) => {
    const mapped = {
      ...user,
      isActive: user.isActive ? "Active" : "Inactive",
      createdOn: DateUtils.formatForExport(
        user.createdOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
    };
    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const exportFilteredData = filteredData.map((user) => {
    const mapped = {
      ...user,
      isActive: user.isActive ? "Active" : "Inactive",
      createdOn: DateUtils.formatForExport(
        user.createdOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
    } as any;
    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = mapped[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Defect Code Books`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {/* {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.ADDCUSTOMERS
          ) ? ( */}
          <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Upload
              </Button>
            }
            component={FaultCategoryForm}
            componentProps={{
              id: 0,
              onRefreshList: fetchData,
            }}
            title="Defect Code Book Form"
            size="default"
          />
          {/* ) : null} */}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            allColumns={initialColumns}
            data={exportData}
            filteredData={exportFilteredData}
            visibleColumns={visibleColumns}
            moduleName="Defect Log Book"
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content",
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
